/**
 * components/BetaApplication/steps/WelcomeStep.tsx
 * Welcome step for the beta application wizard
 *
 * Introduces users to the Beta Program with:
 * - Professional branding and beta program value proposition
 * - Application overview of what will be collected
 * - Clear explanation of the selection process
 * - Call to action to begin application
 */

// 1. External dependencies
import React from 'react';
import { CheckCircle, Users, Zap, Search, MessageSquare, Gift, Award } from 'lucide-react';

// 2. Workspace packages
import { Card, CardHeader, CardBody, BrandMark } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';

/**
 * Welcome step component for beta application
 */
export const WelcomeStep: React.FC = () => {
  /**
   * Application steps overview
   */
  const applicationSteps = [
    'Account Information - email, name, and Discord username',
    'Trading Profile - experience, style, and current tools',
    'Beta Commitment - interest and dedication to testing'
  ];

  /**
   * Beta program highlights
   */
  const betaHighlights = [
    { icon: <Zap className="w-5 h-5" />, text: 'Gain early access to revolutionary trade journaling tools' },
    { icon: <Gift className="w-5 h-5" />, text: `Receive a year of free access to the highest paid tier once ${Brand.shortName} launches` },
    { icon: <Award className="w-5 h-5" />, text: `Permanent recognition as a founding beta member in the ${Brand.name} community` },
  ];

  /**
   * Beta program description
   */
  const betaDescription = [
    { icon: <Users className="w-5 h-5" />, text: 'A small group of traders seeking more from trade journaling' },
    { icon: <Search className="w-5 h-5" />, text: 'Participants will help identify areas for improvement and report any bugs in the beta version' },
    { icon: <MessageSquare className="w-5 h-5" />, text: 'Provide formal weekly feedback and engage anytime in our exclusive beta Discord community' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4">
      <Card 
        className="bg-transparent"
        shadow='none'
        radius='none'      
      >
        {/* Header with Logo and Welcome Messages */}
        <CardHeader className="text-center pb-0">
          <div className="flex flex-col items-center gap-6">
            {/* Logo */}
            <BrandMark size={160} className="p-3" />

            {/* Welcome Messages */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                {Brand.displayName} Beta Program
              </h1>
              <p className="text-lg text-text-secondary">
                Apply to join an exclusive group of traders helping to build the future of trade journaling.
              </p>
            </div>
          </div>
        </CardHeader>

        {/* Body with Card Sections */}
        <CardBody className="gap-6 pt-6">
          {/* What is it? Card */}
          <Card
            className="border border-primary"
            shadow='sm'
          >
            <CardHeader className="bg-primary">
              <h2 className="text-xl font-semibold text-text-inverse">
                What is the {Brand.shortName} Beta Program?
              </h2>
            </CardHeader>
            <CardBody >
              <div className="space-y-4">
                {betaDescription.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <div className="text-primary">
                        {highlight.icon}
                      </div>
                    </div>
                    <p className="text-sm text-text-primary mt-2">
                      {highlight.text}
                    </p>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Why Join Card */}
          <Card
            className="border border-success"
            shadow='sm'
          >
            <CardHeader className="bg-success">
              <h2 className="text-xl font-semibold text-text-inverse">
                Why Join the {Brand.shortName} Beta Program?
              </h2>
            </CardHeader>
            <CardBody >
              <div className="space-y-4">
                {betaHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <div className="text-secondary">
                        {highlight.icon}
                      </div>
                    </div>
                    <p className="text-sm text-text-primary mt-2">
                      {highlight.text}
                    </p>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Application Overview Card */}
          <Card 
            className="border border-secondary"
            shadow='sm'
          >
            <CardHeader className="bg-secondary">
              <h2 className="text-xl font-semibold text-text-inverse ">
                Your application will include:
              </h2>
            </CardHeader>
            <CardBody >
              <div className="space-y-3">
                {applicationSteps.map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-4 h-4 text-success" />
                    </div>
                    <p className="text-sm text-text-primary mt-1">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* Ready to Apply Card */}
          <Card 
            className="border border-default"
            shadow='sm'
          >
            <CardHeader className="bg-default">
              <h3 className="text-lg font-semibold text-text-inverse">
                Ready to Apply?
              </h3>
            </CardHeader>
            <CardBody className="pt-2">
              <div className="space-y-3">
                <p className="text-sm text-text-primary">
                  The application takes about 5-10 minutes to complete. You can save your progress and return later if needed.
                </p>
                <p className="text-xs text-text-secondary">
                  Click "Start Application" below to begin your journey toward becoming a better trader.
                </p>
              </div>
            </CardBody>
          </Card>
        </CardBody>
      </Card>
    </div>
  );
};